import React, { useState, useEffect } from 'react';
import Icon from './Icon';
import { Transaction } from '../types/Transaction';
import './TransactionsTable.css';
import { useApi } from '../services/apiService';
import { formatDateTime, formatSignedMoney } from '../utils/format';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  useReactTable,
} from '@tanstack/react-table';

const PAGE_SIZE = 20;

const columnHelper = createColumnHelper<Transaction>();

const columns = [
  columnHelper.accessor('date', {
    header: ({ column }) => (
      <button
        className="transaction-sort-button"
        onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        aria-label={`Sort by date, currently ${column.getIsSorted() === 'asc' ? 'oldest' : 'newest'} first`}
      >
        Date
        {column.getIsSorted() === 'asc' ? (
          <Icon name="chevronUp" size={12} />
        ) : (
          <Icon name="chevronDown" size={12} />
        )}
      </button>
    ),
    cell: (info) => formatDateTime(info.getValue()),
    enableSorting: true,
  }),
  columnHelper.accessor('description', {
    header: 'Description',
    cell: (info) => info.row.original.merchant_name ?? info.getValue(),
  }),
  columnHelper.accessor('category_name', {
    header: 'Category',
    cell: (info) => (
      <span className="transaction-chip">
        {info.getValue() ?? 'Uncategorized'}
      </span>
    ),
  }),
  columnHelper.accessor('connection', {
    header: 'Bank',
    cell: (info) => {
      const connection = info.getValue();
      if (!connection) return null;
      return (
        <span className="transaction-bank">
          {connection.logo ? (
            <img
              src={connection.logo}
              alt=""
              className="transaction-bank-logo"
            />
          ) : (
            <span
              className="transaction-bank-logo transaction-bank-fallback"
              aria-hidden="true"
            >
              {connection.name?.slice(0, 3).toUpperCase()}
            </span>
          )}
          {connection.name}
        </span>
      );
    },
  }),
  //todo: amount comes back from the backend as a string, so coerce it here until that is fixed
  columnHelper.accessor((row) => Number(row.amount), {
    id: 'amount',
    header: () => <span className="transaction-header-amount">Amount</span>,
    cell: (info) => (
      <span className={`num${info.getValue() >= 0 ? ' is-credit' : ''}`}>
        {formatSignedMoney(info.getValue())}
      </span>
    ),
  }),
];

const TransactionsTable: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[] | null>(null);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });

  const [totalRecords, setTotalRecords] = useState(0);
  const [refreshed, setRefreshed] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { getTransactions, refreshTransactions } = useApi();

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        const data = await getTransactions(pagination.pageIndex + 1);
        setTransactions(data.data);
        setTotalRecords(data.totalRecords);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch transactions:', err);
        setError('Failed to fetch transactions.');
      }
    };

    fetchTransactions();
  }, [pagination.pageIndex, refreshed]);

  const table = useReactTable({
    data: transactions || [],
    columns,
    state: {
      pagination,
    },
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      sorting: [{ id: 'date', desc: true }],
    },
    manualPagination: true, // Tells the table not to paginate the data itself
    rowCount: totalRecords,
  });

  const onRefreshTransactions = async () => {
    setRefreshing(true);
    try {
      await refreshTransactions();
      setRefreshed(!refreshed);
    } catch (err) {
      console.error('Failed to refresh transactions:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const firstRecord =
    totalRecords === 0 ? 0 : pagination.pageIndex * PAGE_SIZE + 1;
  const lastRecord = Math.min(
    totalRecords,
    (pagination.pageIndex + 1) * PAGE_SIZE,
  );

  return (
    <section
      className="card transactions-table-container"
      aria-labelledby="transactions-title"
    >
      <div className="section-header">
        <div>
          <h2 id="transactions-title" className="card-title">
            All transactions
          </h2>
          <p className="card-subtitle">
            {totalRecords.toLocaleString('en-NZ')} transactions · transfers
            excluded
          </p>
        </div>
        <button
          className="btn"
          onClick={onRefreshTransactions}
          disabled={refreshing}
        >
          <Icon
            name="refresh"
            size={16}
            className={refreshing ? 'spin' : undefined}
          />
          {refreshing ? 'Refreshing…' : 'Refresh transactions'}
        </button>
      </div>

      {error && <p className="error">{error}</p>}
      {!transactions && !error && (
        <p className="muted">Loading transactions...</p>
      )}

      <div className="transaction-table-scroll">
        <table className="transaction-table">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    scope="col"
                    className={`transaction-header transaction-col-${header.column.id}`}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="transaction-row">
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={`transaction-cell transaction-col-${cell.column.id}`}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <span>
          Showing {firstRecord}–{lastRecord} of{' '}
          {totalRecords.toLocaleString('en-NZ')}
        </span>
        <nav className="pagination-controls" aria-label="Pagination">
          <button
            className="btn"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            Previous
          </button>
          <span className="pagination-page" aria-current="page">
            Page {table.getState().pagination.pageIndex + 1} of{' '}
            {Math.max(1, table.getPageCount())}
          </span>
          <button
            className="btn"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            Next
          </button>
        </nav>
      </div>
    </section>
  );
};

export default TransactionsTable;
